import { Box, Paper, Skeleton, Stack, Typography } from "@mui/material";
import {
  CalendarMonthRounded,
  CardGiftcardRounded,
  LocationOnRounded,
  PersonRounded,
} from "@mui/icons-material";
import { useCallback, useEffect, useState } from "react";
import apiService from "../../../../../../service/Api";
import { IStreamDetails } from "../../../../../../types/streams";
import DynamicThumbnailCard from "../../../../../stream-management/components/thumbnail/ThumbnailCard";

interface StreamSummaryCardProps {
  streamId: number | null;
}

interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function InfoRow({ icon, label, value }: InfoRowProps) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "8px",
          bgcolor: "#f7f0ed",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography
          sx={{ fontSize: "11px", color: "text.secondary", lineHeight: 1 }}
        >
          {label}
        </Typography>
        <Typography
          sx={{ fontSize: "13px", fontWeight: 700, color: "#1e293b", mt: 0.3 }}
        >
          {value}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function StreamSummaryCard({
  streamId,
}: StreamSummaryCardProps) {
  const [streamDetails, setStreamDetails] = useState<IStreamDetails | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetchStream = useCallback(async () => {
    if (!streamId) return;
    setIsLoading(true);
    setError(false);
    try {
      const res = await apiService.get<IStreamDetails>(
        `/api/streams/stream/${streamId}/`,
      );
      setStreamDetails(res.data);
    } catch {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [streamId]);

  useEffect(() => {
    fetchStream();
  }, [fetchStream]);

  const deceased = streamDetails?.booking?.deceased?.[0];

  const deceasedName = deceased
    ? `${deceased.first_name} ${deceased.surname}`
    : (streamDetails?.title ?? "N/A");

  const years =
    deceased?.date_of_birth && deceased?.date_of_death
      ? `${new Date(deceased.date_of_birth).getFullYear()} – ${new Date(deceased.date_of_death).getFullYear()}`
      : null;

  const streamDate = streamDetails?.date
    ? new Date(streamDetails.date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : "N/A";

  const packageName = streamDetails?.booking?.package_item?.name ?? "N/A";

  const contactPerson = streamDetails?.booking?.contact
    ? `${streamDetails.booking.contact.first_name} ${streamDetails.booking.contact.last_name}`
    : "N/A";

  if (!streamId) return null;

  return (
    <Paper sx={{ borderRadius: 3, p: 2.5, boxShadow: 0, mb: 2 }}>
      {isLoading && (
        <Stack spacing={1.5}>
          <Skeleton variant="text" width="60%" height={28} />
          <Skeleton variant="text" width="30%" height={20} />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={1}>
            <Stack spacing={1.5} flex={1}>
              <Skeleton variant="rounded" height={40} />
              <Skeleton variant="rounded" height={40} />
              <Skeleton variant="rounded" height={40} />
              <Skeleton variant="rounded" height={40} />
            </Stack>
            <Skeleton
              variant="rounded"
              sx={{
                width: { xs: "100%", sm: 160 },
                height: { xs: 80, sm: 120 },
              }}
            />
          </Stack>
        </Stack>
      )}

      {error && !isLoading && (
        <Typography
          sx={{
            color: "text.secondary",
            fontSize: "13px",
            textAlign: "center",
          }}
        >
          Failed to load stream details.
        </Typography>
      )}

      {!isLoading && !error && streamDetails && (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2.5}
          alignItems="flex-start"
        >
          <Box sx={{ flex: 1, width: "100%" }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "17px",
                color: "#1e293b",
                lineHeight: 1.2,
              }}
            >
              {deceasedName}
            </Typography>
            {years && (
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "text.secondary",
                  mt: 0.3,
                  mb: 2,
                }}
              >
                {years}
              </Typography>
            )}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1.5,
                mt: years ? 0 : 2,
              }}
            >
              <InfoRow
                icon={
                  <CalendarMonthRounded
                    sx={{ fontSize: 16, color: "#724e41" }}
                  />
                }
                label="Stream Date"
                value={streamDate}
              />
              <InfoRow
                icon={
                  <LocationOnRounded sx={{ fontSize: 16, color: "#724e41" }} />
                }
                label="Venue"
                value={
                  streamDetails?.service?.venue?.name ??
                  streamDetails?.address_one ??
                  "N/A"
                }
              />
              <InfoRow
                icon={
                  <CardGiftcardRounded
                    sx={{ fontSize: 16, color: "#724e41" }}
                  />
                }
                label="Package"
                value={packageName}
              />
              <InfoRow
                icon={<PersonRounded sx={{ fontSize: 16, color: "#724e41" }} />}
                label="Contact Person"
                value={contactPerson}
              />
            </Box>
          </Box>

          <Box
            sx={{
              flexShrink: 0,
              width: { xs: "100%", sm: 200 },
              height: { xs: "auto", sm: 113 },
              borderRadius: "10px",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                transformOrigin: "top left",
                transform: `scale(${200 / 350})`,
                pointerEvents: "none",
                display: { xs: "none", sm: "block" },
              }}
            >
              <DynamicThumbnailCard
                refreshTrigger={0}
                streamData={streamDetails}
                streamId={streamDetails.id}
                height={Math.round((350 * 720) / 1280)}
                width={350}
              />
            </Box>
          </Box>
        </Stack>
      )}
    </Paper>
  );
}
